<script setup lang="ts">
import { computed, shallowRef, watchEffect } from 'vue'
import { EVariants, type IInfobox } from './types'
import useInfoboxConfig from './composables/useInfoboxConfig'
import { isDark } from '@/lib/utils'

const props = withDefaults(defineProps<IInfobox>(), {
    icon: true,
    titleHeadingLevel: 'h3',
})

const infoboxConfig = shallowRef({} as Record<string, string>)

watchEffect(() => {
    infoboxConfig.value = useInfoboxConfig(props)
})

// ICONS
const defaultIcons: Record<EVariants, string> = {
    [EVariants.NEUTRAL]: '',
    [EVariants.INFORMATIVE]: 'info',
    [EVariants.WARNING]: 'validation-warning',
    [EVariants.ERROR]: 'validation-error',
    [EVariants.SUCCESS]: 'validation-success',
}

const iconArialabels: Record<EVariants, string> = {
    [EVariants.NEUTRAL]: '',
    [EVariants.INFORMATIVE]: 'Informacja',
    [EVariants.WARNING]: 'Ostrzeżenie',
    [EVariants.ERROR]: 'Błąd',
    [EVariants.SUCCESS]: 'Sukces',
}

function getIconName(variant: EVariants | `${EVariants}`, iconName?: string): string {
    return iconName || defaultIcons[variant]
}

const iconName = computed(() => getIconName(props.variant, props.iconName))
</script>

<template>
    <div
        :class="[isDark(dark), infoboxConfig.rootClasses]"
        role="status"
        data-gpp-tools-ui
    >
        <div class="flex p-2">
            <div
                v-if="icon && iconName"
                class="mr-4 shrink-0"
            >
                <slot name="icon">
                    <VIcon
                        :name="iconName"
                        size="MEDIUM"
                        data-test="infobox-icon"
                        :aria-label="iconArialabels[variant]"
                        :class="infoboxConfig.iconClasses"
                    />
                </slot>
            </div>
            <div>
                <component
                    :is="titleHeadingLevel"
                    v-if="title"
                    class="heading-2xs mb-2"
                >
                    {{ title }}
                </component>
                <div class="paragraph-md">
                    <slot />
                </div>
            </div>
        </div>
    </div>
</template>
